import { ArrowBack } from "@mui/icons-material";
import { Box, Button, Stack, styled } from "@mui/material";
import logo from "Assets/Images/incendium-colour-logo.png";
import Image from "mui-image";
import GlassCard from "Components/GlassCard/GlassCard";
import OnboardingProgress from "features/onboarding/components/OnboardingProgress";
import OnboardingStepHeading from "features/onboarding/components/OnboardingStepHeading";
import { ReactNode } from "react";

const PageRoot = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: theme.palette.grey[50],
  padding: theme.spacing(4, 6),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
}));

const BackButton = styled(Button)(() => ({
  paddingLeft: 0,
  alignSelf: "flex-start",
}));

export interface IOnboardingLayoutPanel {
  heading: string;
  headingAccent?: string;
  paragraphs: string[];
}

interface IOnboardingLayoutProps {
  stepNumber: number;
  totalSteps: number;
  panel: IOnboardingLayoutPanel;
  onBack?: () => void;
  showBack?: boolean;
  showProgress?: boolean;
  children: ReactNode;
}

function OnboardingLayout({
  stepNumber,
  totalSteps,
  panel,
  onBack,
  showBack = true,
  showProgress = true,
  children,
}: IOnboardingLayoutProps) {
  const hasLeftContent = Boolean(panel.heading);

  return (
    <PageRoot>
      <Image
        width={170}
        src={logo}
        duration={50}
        fit={"contain"}
        objectPosition={"left"}
      />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "center",
          width: "100%",
          mt: { xs: 2, md: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            flexDirection: { xs: "column", md: "row" },
            gap: 6,
            maxWidth: { xs: "100%", md: 1200 },
            mx: "auto",
            width: "100%",
            maxHeight: { xs: "none", md: "calc(100vh - 160px)" },
          }}
        >
          <Stack
            spacing={4}
            sx={{
              flex: { xs: 0, md: "0 0 380px" },
              alignSelf: "stretch",
              justifyContent: "space-between",
            }}
          >
            <Stack spacing={4}>
              {showProgress && (
                <OnboardingProgress
                  stepNumber={stepNumber}
                  totalSteps={totalSteps}
                />
              )}
              {hasLeftContent && (
                <OnboardingStepHeading
                  heading={panel.heading}
                  headingAccent={panel.headingAccent}
                  paragraphs={panel.paragraphs}
                />
              )}
            </Stack>
            {showBack && stepNumber > 1 && onBack && (
              <BackButton
                startIcon={<ArrowBack />}
                onClick={onBack}
                variant="text"
              >
                Back
              </BackButton>
            )}
          </Stack>
          <GlassCard
            sx={{
              flex: 1,
              minWidth: 0,
              height: { xs: "auto", md: "calc(100vh - 160px)" },
              maxHeight: { xs: "none", md: 800 },
              minHeight: 480,
              display: "flex",
              flexDirection: "column",
            }}
            boxProps={{
              py: 4,
              px: 4,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 400,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {children}
            </Box>
          </GlassCard>
        </Box>
      </Box>
    </PageRoot>
  );
}

export default OnboardingLayout;
